<template>
  <div
    v-if="hasActiveSubTargets"
    v-show="!disable"
    :class="{ 'focused-target': focused }"
  >
    <!-- v-show, not v-if, on `disable`: a session pause sets it, and unmounting would drop
         IntervalTimer before its sessionPaused watcher freezes the target's timer. -->
    <div class="row text-center full-width q-px-sm q-pt-sm">
      <div
        :class="{ 'text-uppercase text-dark-variant interval-overline': tiny }"
        class="text-overline row items-center"
      >
        <q-chip
          v-if="!tiny"
          size="sm"
          :text-color="getTextColor(getTargetWorkflowProperty('color', ''))"
          :color="getTargetWorkflowProperty('color', '')"
          :style="{ backgroundColor: getTargetWorkflowProperty('color', '') }"
          :icon="getTargetWorkflowProperty('icon', 'help')"
          class="chip-no-label"
        />
        {{ target.label }}
      </div>
    </div>

    <div class="row q-pa-sm items-stretch no-wrap">
      <div class="col-auto q-mr-md">
        <interval-timer
          v-if="hasTimer"
          ref="timer"

          :model-value="localCurrentInterval"
          :interval-time="target.interval"
          :duration="localDuration"
          :timer-interval-pause="target.timerIntervalPause"

          :paused="localIsPaused"
          :duration-offset="timerOffsetTimestamp"

          :session-program="sessionProgram.id"

          :session-paused="sessionPaused"
          :session-duration-offset="sessionDurationOffset"

          :disable="disable"
          :dense="dense"
          :tiny="tiny"
          :warning="intervalWarning"
          :warning-sound="intervalWarningSound"
          :ios-audio="iosAudio"
          :mute-audio="muteAudio"
          class="bg-primary-light full-height"

          @update:model-value="onRecordIntervalTimer"
          @start="onResumeIntervalTimer"
          @pause="onPauseIntervalTimer"
          @update="onUpdateIntervalTimer"
        />
      </div>

      <div
        class="row col q-pt-md"
        :class="{
          'no-wrap': dense && tiny,
          'q-col-gutter-sm': dense && tiny,
          'q-col-gutter-md': !dense || !tiny
        }"
      >
        <span
          v-for="subTarget in sortedTargets"
          :key="subTarget.id"
        >
          <target-data
            ref="targets"
            :session-program="sessionProgram"
            :target="subTarget"
            :data="adjustedData"
            :active-prompt-levels="activePromptLevels"
            :workflow-statuses="workflowStatuses"
            :prevent-duplicates-on-trial="preventDuplicatesOnTrial"
            :session-paused="sessionPaused"
            :session-duration-offset="sessionDurationOffset"
            :dense="dense"
            :tiny="tiny"
            :disable="disable"

            @record="onRecord"
            @record-multiple-data="onRecordMultipleData"
            @record-program-data="onRecordProgramData"
          />
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import IntervalTimer from 'components/Programs/DataCollection/IntervalTimer.vue';
import { Timestamp } from 'firebase/firestore';
import { chain, get, isEmpty, isObject } from 'lodash';
import { mapState } from 'pinia';
import DataCollection from 'src/mixins/DataCollection';
import { sessions } from 'src/store/sessions';
import { getTextColor } from 'src/utils/TargetUtils';
import { defineAsyncComponent } from 'vue';

export default {
  name: 'IntervalTarget',

  components: {
    IntervalTimer,
    TargetData: defineAsyncComponent(() => import('components/Programs/DataCollection/TargetData')),
  },

  mixins: [DataCollection],

  props: {
    iosAudio: {
      type: Audio,
      default: null,
    },
  },

  emits: ['record-target', 'record-program-data', 'record-multiple-data'],

  data() {
    return {
      localCurrentInterval: 1,
      localDuration: 0,
      localIsPaused: false,
      localOffset: null,
    };
  },

  computed: {
    ...mapState(sessions, ['currentVideoCallSession']),

    adjustedData() {
      return {
        ...this.data,
        currentInterval: this.localCurrentInterval,
      };
    }, // adjustedData()

    sortedTargets() {
      return get(this.target, 'subTargets', []);
    }, // sortedTargets()

    hasDataObject() {
      return isObject(this.data);
    }, // hasDataObject()

    // A target disabled outside a pause (supervision, a session run by somebody else) keeps no
    // timer: it would spin a web worker and play interval warning sounds for a read-only viewer.
    hasTimer() {
      return this.hasDataObject && (!this.disable || this.sessionPaused);
    }, // hasTimer()

    // Don't play sounds if we are in a video call - it will interfere with the call
    muteAudio() {
      return !!this.currentVideoCallSession;
    }, // muteAudio()

    timerOffsetTimestamp() {
      if (chain(this.localOffset).get('toDate').isFunction().value()) {
        return this.localOffset.toDate();
      }
      return null;
    }, // timerOffsetTimestamp()

    intervalWarning() {
      if (!get(this.target, 'timerWarning', false)) {
        return null;
      }
      return get(this.target, 'timerWarningSeconds', 0);
    }, // intervalWarning()

    intervalWarningSound() {
      if (!get(this.target, 'timerWarning', false)) {
        return '';
      }
      return get(this.target, 'timerWarningSound', '');
    }, // intervalWarningSound()

    hasActiveSubTargets() {
      if (isEmpty(this.sortedTargets)) {
        return false;
      }
      const activeWorkflowTypes = ['probe', 'acquisition', 'mastered'];

      return this.sortedTargets.some((subTarget) => {
        const workflow = this.getAdjustedWorkflow(
          this.workflowStatuses,
          get(this.sessionProgram, 'program'),
          subTarget,
          this.target,
        );

        const statusType = get(workflow, [subTarget.status, 'type']);

        return activeWorkflowTypes.includes(statusType);
      });
    }, // hasActiveSubTargets()
  },

  watch: {
    data: {
      immediate: true,
      deep: true,
      handler(newData) {
        const { id } = this.target;
        this.localCurrentInterval = get(newData, `currentInterval_${id}`) || get(newData, 'currentInterval') || 1;
        this.localDuration = get(newData, `duration_${id}`) || get(newData, 'duration') || 0;
        this.localIsPaused = get(newData, `isTimerPaused_${id}`)
          ?? get(newData, 'isTimerPaused')
          ?? (this.target.timerAutoStart === false);
        this.localOffset = get(newData, `timerOffsetTimestamp_${id}`) || get(newData, 'timerOffsetTimestamp');
      },
    },
  },

  methods: {
    getTextColor,

    onRecordIntervalTimer(currentInterval, isTimerPaused = false, timerOffsetTimestamp = Timestamp.now()) {
      if (this.disable) {
        return;
      }

      const dataObj = {
        [`currentInterval_${this.target.id}`]: currentInterval,
        [`duration_${this.target.id}`]: 0,
        [`timerOffsetTimestamp_${this.target.id}`]: timerOffsetTimestamp,
        [`isTimerPaused_${this.target.id}`]: isTimerPaused,
      };

      this.$emit('record-program-data', this.sessionProgram, dataObj);
    }, // onRecordIntervalTimer()

    onUpdateIntervalTimer(duration, timerOffsetTimestampDate = null) {
      const timerOffsetTimestamp = (timerOffsetTimestampDate !== null)
        ? Timestamp.fromDate(timerOffsetTimestampDate)
        : null;

      const dataObj = {
        [`duration_${this.target.id}`]: duration,
        [`timerOffsetTimestamp_${this.target.id}`]: timerOffsetTimestamp,
      };

      this.$emit('record-program-data', this.sessionProgram, dataObj);
    }, // onUpdateIntervalTimer()

    onPauseIntervalTimer(duration) {
      if (this.disable) {
        return;
      }

      const dataObj = {
        [`duration_${this.target.id}`]: duration,
        [`timerOffsetTimestamp_${this.target.id}`]: null,
        [`isTimerPaused_${this.target.id}`]: true,
      };

      this.$emit('record-program-data', this.sessionProgram, dataObj);
    }, // onPauseIntervalTimer()

    onResumeIntervalTimer(duration) {
      const dataObj = {
        [`duration_${this.target.id}`]: duration,
        [`timerOffsetTimestamp_${this.target.id}`]: Timestamp.now(),
        [`isTimerPaused_${this.target.id}`]: false,
      };

      this.$emit('record-program-data', this.sessionProgram, dataObj);
    }, // onResumeIntervalTimer()

    onRecord(sessionProgram, subTarget, dataFromChild) {
      if (!dataFromChild) return;

      const extendedData = {
        ...dataFromChild,
        trial: dataFromChild.trial || this.localCurrentInterval,
      };
      this.$emit('record-target', subTarget, extendedData);
    }, // onRecord()

    onRecordMultipleData(sessionProgram, dataArr, target) {
      this.$emit('record-multiple-data', dataArr);
    }, // onRecordMultipleData()

    onRecordProgramData(sessionProgram, dataObj) {
      this.$emit('record-program-data', sessionProgram, dataObj);
    }, // onRecordProgramData()

    finishSession() {
      const finalData = chain(this.$refs)
        .get('targets')
        .map((targetRef) => {
          if (chain(targetRef).get('finishSession').isFunction().value()) {
            return targetRef.finishSession();
          }
          return null;
        })
        .flatten()
        .compact()
        .value();

      if (chain(this.$refs).get(['timer', 'finishSession']).isFunction().value()) {
        this.$refs.timer.finishSession();
      }

      return finalData;
    }, // finishSession()
  },
};
</script>

<style scoped>
.interval-overline {
  font-size: 7px;
  line-height: 1rem;
}
:deep(span.overflow-hidden) {
  overflow: visible !important;
}
</style>
